import React, { FunctionComponent, useEffect, useMemo, useState } from 'react';
import '../productPage/producthome.css';
import { Box } from '@mui/material';
import * as styles from '../productPage/tableComponents/ProductPage_style';
import { useLocation, useNavigate } from 'react-router-dom';
import BreadCrumbs from '../common/BreadCrumbs';
import SocialPage from './SocialPage';
import SupplyChainPage from './SupplyChainPage';
import EnvironmentalPage from './EnvironmentalPage';
import GovernancePage from './GovernancePage';
import TechnicalResources from '../automotivePage/TechnicalResources';
import TabsComponent, { GetResponsiveWidth } from '../common/TabsComponent';
import { getTabTypography } from '../investorPage/investorRelationHome';
import ReactiveBanner from '../common/ReactiveBanner';
import { PrefilteredResourceLibraryItemsProvider } from '../../graphql/pimcore/modules/resourceLibrary/contexts/PrefilteredResourceLibraryItemsProvider';
import { AboutSustainabilityContent } from '../../graphql/pimcore/modules/siteContent/types/AboutSustainabilityContent';
import QualityPage from './QualityPage';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function CustomTabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`product-tabpanel-${index}`}
            aria-labelledby={`product-tab-${index}`}
            {...other}
        >
            {value === index && <Box>{children}</Box>}
        </div>
    );
}

interface SustainabilityHomeProps {
    cmsContent: AboutSustainabilityContent;
}

const SustainabilityHome: FunctionComponent<SustainabilityHomeProps> = ({ cmsContent }) => {
    const [url, setUrl] = useState(0);
    const location = useLocation();
    const navigate = useNavigate();
    const [openMenu, setOpenMenu] = React.useState(false);
    const pathMap = useMemo(
        () => [
            '/About/Sustainability/Governance',
            '/About/Sustainability/Social',
            '/About/Sustainability/SupplyChain',
            '/About/Sustainability/Environment',
            '/About/Sustainability/Quality',
            '/About/Sustainability/Resources',
        ],
        []
    );

    useEffect(() => {
        const matchedIndex = pathMap.findIndex((path) => location.pathname.startsWith(path));
        setUrl(matchedIndex >= 0 ? matchedIndex : 0);
    }, [location.pathname, pathMap]);

    const handleChange = (event: React.SyntheticEvent | null, newUrl: number) => {
        setUrl(newUrl);
        navigate(pathMap[newUrl] || pathMap[0]!);
    };

    return (
        <div key={location.pathname} style={styles.productPageContainer}>
            <div style={styles.breadCrumbs}>
                <Box
                    sx={{
                        width: GetResponsiveWidth,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        justifyContent: 'center',
                        margin: 'auto',
                    }}
                >
                    <BreadCrumbs navigationPath="/Home/About/Sustainability" />
                </Box>
            </div>
            <ReactiveBanner
                title={cmsContent.pageTitle.content}
                description={cmsContent.pageSubtitle.content}
                bannerImage={cmsContent.pageTitleBackgroundImage.imageUrl}
            />
            <Box sx={{ width: '100%', backgroundColor: '#7A7C81' }} data-testid={'outter_box'}>
                <TabsComponent
                    isMenuExpandMoreIcon
                    url={url}
                    setOpenMenu={setOpenMenu}
                    openMenu={openMenu}
                    handleChange={handleChange}
                    menuItems={[
                        { label: cmsContent.Governance.title.content, value: 0 },
                        { label: cmsContent.Social.title.content, value: 1 },
                        { label: cmsContent.SupplyChain.title.content, value: 2 },
                        { label: cmsContent.Environmental.title.content, value: 3 },
                        { label: cmsContent.Quality.title.content, value: 4 },
                        { label: cmsContent.Resources.title.content, value: 5 },
                    ]}
                    tabTypography={{
                        ...getTabTypography,
                    }}
                />

                <CustomTabPanel value={url} index={0}>
                    <GovernancePage cmsContent={cmsContent.Governance} />
                </CustomTabPanel>
                <CustomTabPanel value={url} index={1}>
                    <Box sx={{ width: '100%', backgroundColor: 'white' }}>
                        <SocialPage cmsContent={cmsContent.Social} />
                    </Box>
                </CustomTabPanel>
                <CustomTabPanel value={url} index={2}>
                    <Box sx={{ width: '100%', backgroundColor: 'white' }}>
                        <SupplyChainPage cmsContent={cmsContent.SupplyChain} />
                    </Box>
                </CustomTabPanel>
                <CustomTabPanel value={url} index={3}>
                    <Box sx={{ width: '100%', backgroundColor: 'white' }}>
                        <EnvironmentalPage cmsContent={cmsContent.Environmental} />
                    </Box>
                </CustomTabPanel>
                <CustomTabPanel value={url} index={4}>
                    <Box sx={{ width: '100%', backgroundColor: 'white' }}>
                        <QualityPage cmsContent={cmsContent.Quality} />
                    </Box>
                </CustomTabPanel>
                <CustomTabPanel value={url} index={5}>
                    <PrefilteredResourceLibraryItemsProvider
                        initialFilters={{
                            resourceTypeKeys: [
                                'Governance/ESG',
                                'Product Environmental Compliance',
                                'Quality Documents - Facility',
                                'Quality Documents - Product',
                                'Supply Chain',
                            ],
                        }}
                    >
                        <TechnicalResources backgroundColor={'white'} />
                    </PrefilteredResourceLibraryItemsProvider>
                </CustomTabPanel>
            </Box>
        </div>
    );
};

export default SustainabilityHome;
